import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AlertsService, ModalService } from '@clavisco/alerts';
import { StorageService } from '@clavisco/core';
import { CL_CHANNEL, ICLCallbacksInterface, ICLEvent, LinkerService, Register, Run, StepDown } from '@clavisco/linker';
import { IMenuNode } from '@clavisco/menu';
import { PermissionService } from 'src/app/core/services/permission.service';
import { Structures } from '@clavisco/core';
import ICLResponse = Structures.Interfaces.ICLResponse;
import { IPermission } from '../../core/interfaces/i-permission';
import { DataStorageService } from 'src/app/core/services/data-storage.service';
import { IToken } from 'src/app/core/interfaces/i-token';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit {

  /** Identifier for the menu, used in the linker system */
  MenuId: string = 'menuId';

  /** Logo path shown in the sidebar */
  Logo: string = '/assets/colcarnes-wt.png';

  /** Welcome message or title displayed in the menu */
  Title: string = 'Bienvenido';

  /** Currently logged-in user */
  User!: IToken;

  /** Toggle for the menu's collapsed/expanded state */
  Toggle: Boolean = true;

  /** List of nodes displayed in the menu */
  MenuNodes: IMenuNode[] = [];

  /** Callbacks for the linker system */
  callbacks: ICLCallbacksInterface<CL_CHANNEL> = {
    Callbacks: {},
    Tracks: []
  };

  /** List of permissions for the current user's role */
  permsList: IPermission[] = [];

  /** Displayed company text */
  txtcompany: string = 'company';

  /** Action text (optional) */
  txtaccion: string = '';

  constructor(
    private router: Router,
    @Inject('LinkerService') private linkerService: LinkerService,
    private dataStorageService: DataStorageService,
    private permsService: PermissionService,
    private alertService: AlertsService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
  ) {
    // Initial static menu structure
    this.MenuNodes = [
      {
        Key: "home",
        Description: "Inicio",
        Icon: "house",
        Route: "/home",
        Visible: true,
        Nodes: []
      },
      {
        Key: "documents",
        Description: "Documentos",
        Icon: "folder_open",
        Route: "",
        Visible: true,
        Nodes: [
          {
            Key: "collection_View",
            Description: "Consultar Planillas",
            Icon: "",
            Route: "/collectionView",
            Visible: true,
            Nodes: []
          },
          {
            Key: "spreadsheet_Creation",
            Description: "Creación de Planillas",
            Icon: "",
            Route: "/spreadsheetCreation",
            Visible: true,
            Nodes: []
          }
        ]
      },
      {
        Key: "settings",
        Description: "Configuración",
        Icon: "settings_suggest",
        Route: "",
        Visible: true,
        Nodes: [
          {
            Key: "users",
            Description: "Usuarios",
            Icon: "",
            Route: "/users",
            Visible: true,
            Nodes: []
          },
          {
            Key: "role_perms",
            Description: "Roles & Permisos",
            Icon: "",
            Route: "/rolePerm",
            Visible: true,
            Nodes: []
          }
        ]
      },
      {
        Key: "logout",
        Description: "Cerrar sesión",
        Icon: "logout",
        Route: "/login",
        Visible: true,
        Nodes: []
      },
    ];
  }

  /**
   * Angular lifecycle method.
   * Initializes user data, permissions, and registers the menu in the linker system.
   */
  ngOnInit(): void {
    this.User = this.dataStorageService.GetItem<IToken>('currentUser');
    this.GetPermissionsByRol();
    Register(this.MenuId, CL_CHANNEL.OUTPUT, this.ClickMenuOption, this.callbacks);
    this.linkerService.Flow()?.pipe(
      StepDown<CL_CHANNEL>(this.callbacks),
    ).subscribe({
      next: callback => Run(callback.Target, callback, this.callbacks.Callbacks),
      error: error => console.log(`Error:`, error)
    });
    this.cdr.detectChanges();
  }

  /**
   * Retrieves the permissions associated with the current user's role.
   * Applies those permissions to the menu visibility.
   */
  GetPermissionsByRol(): void {
    this.permsList.length = 0;
    this.permsService.GetPermissionsByRol(Number(this.User?.rolId)).subscribe({
      next: (response: ICLResponse<IPermission[]>) => {
        if (response.Data.length > 0) {
          this.dataStorageService.SetItem<IPermission[]>('permissions', response.Data);
          this.permsList = response.Data;
          this.HandleMenuPermission(this.permsList);
          this.linkerService.Publish({
            CallBack: CL_CHANNEL.INFLATE,
            Target: this.MenuId,
            Data: JSON.stringify(this.MenuNodes)
          } as ICLEvent);
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.alertService.ShowAlert({ HttpErrorResponse: error });
      }
    });
  }

  /**
   * Callback method triggered when a menu option is selected.
   * @param _event Event containing menu option data.
   */
  ClickMenuOption = (_event: ICLEvent): void => {
    const menuNode: IMenuNode = JSON.parse(_event.Data) as IMenuNode;
    switch (menuNode.Key) {
      case "logout":
      //  this.aut.logout();
    }
  }

  /**
   * Toggles the visibility (expanded/collapsed) state of the menu.
   * Also publishes the state via the linker system.
   */
  ToggleMenu(): void {
    this.Toggle = !this.Toggle;
    this.linkerService.Publish({
      CallBack: CL_CHANNEL.DATA_LINE_1,
      Target: this.MenuId,
      Data: JSON.stringify(this.Toggle)
    } as ICLEvent);
  }

  /**
   * Applies permission visibility to each menu node based on the given permissions.
   * @param permission List of permissions for the current role.
   */
  HandleMenuPermission(permission: IPermission[]): void {
    this.MenuNodes.forEach((node: IMenuNode) => {
      if (node.Key === 'home' || node.Key === 'logout') {
        node.Visible = true;
      } else {
        node.Visible = this.CheckPermission(this.GetRequiredPermission(node.Key), permission);
      }
      if (node.Nodes.length > 0) {
        node.Nodes.forEach((subNode: IMenuNode) => {
          subNode.Visible = this.CheckPermission(this.GetRequiredPermission(subNode.Key), permission);
        });
      }
    });
  }

  /**
   * Checks if a specific permission key exists in the permission list.
   * @param Key The permission key to check.
   * @param permission The list of permissions to evaluate.
   * @returns True if permission exists, false otherwise.
   */
  CheckPermission(Key: string, permission: IPermission[]): boolean {
    return permission.some((perm: IPermission) => perm.Name === Key);
  }

  /**
   * Gets the required permission name for a given menu key.
   * @param key The key of the menu option.
   * @returns The corresponding permission name.
   */
  GetRequiredPermission(key: string): string {
    let requiredPerm: string = '';
    switch (key) {
      case 'documents':
        requiredPerm = 'M_Documents';
        break;
      case 'collection_View':
        requiredPerm = 'S_CollectionView';
        break;
      case 'spreadsheet_Creation':
        requiredPerm = 'S_SpreadCreation';
        break;
      case 'settings':
        requiredPerm = 'M_Settings';
        break;
      case 'users':
        requiredPerm = 'S_Users';
        break;
      case 'role_perms':
        requiredPerm = 'S_RolsPerm';
        break;
    }
    return requiredPerm;
  }
}